import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { getPurchaseOrders } from '../api';
import { PurchaseOrder, PurchaseOrderStatus } from '../types/purchaseOrder';
import { formatDate, formatCurrency } from '../utils';

const Dashboard: React.FC = () => {
  const [chartData14Days, setChartData14Days] = useState({
    labels: ['Pending', 'In Progress', 'Completed', 'Rejected'],
    datasets: [
      {
        data: [0, 0, 0, 0],
        backgroundColor: ['#FF9900', '#36A2EB', '#4BC0C0', '#FF4444'],
        hoverBackgroundColor: ['#FFB74D', '#64B5F6', '#81C784', '#FF6666']
      }
    ]
  });

  const [chartData30Days, setChartData30Days] = useState({
    labels: ['Pending', 'In Progress', 'Completed', 'Rejected'],
    datasets: [
      {
        data: [0, 0, 0, 0],
        backgroundColor: ['#FF9900', '#36A2EB', '#4BC0C0', '#FF4444'],
        hoverBackgroundColor: ['#FFB74D', '#64B5F6', '#81C784', '#FF6666']
      }
    ]
  });

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const isWithinLast14Days = (date: string | Date) => {
    const orderDate = new Date(date);
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    return orderDate >= fourteenDaysAgo;
  };

  const isWithinLast30Days = (date: string | Date) => {
    const orderDate = new Date(date);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return orderDate >= thirtyDaysAgo;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [orders] = await getPurchaseOrders({
        page: 1,
        limit: 1000,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: searchTerm
      });

      // Filter orders for the last 14 days
      const recentOrders14Days = orders.filter(order => isWithinLast14Days(order.createdAt));
      const statusCounts14Days = recentOrders14Days.reduce((acc: { [key: string]: number }, order: PurchaseOrder) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
      }, {});

      // Filter orders for the last 30 days
      const recentOrders30Days = orders.filter(order => isWithinLast30Days(order.createdAt));
      const statusCounts30Days = recentOrders30Days.reduce((acc: { [key: string]: number }, order: PurchaseOrder) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
      }, {});

      setChartData14Days(prevData => ({
        ...prevData,
        datasets: [{
          ...prevData.datasets[0],
          data: [
            statusCounts14Days[PurchaseOrderStatus.Pending] || 0,
            statusCounts14Days[PurchaseOrderStatus.InProgress] || 0,
            statusCounts14Days[PurchaseOrderStatus.Completed] || 0,
            statusCounts14Days[PurchaseOrderStatus.Rejected] || 0
          ]
        }]
      }));

      setChartData30Days(prevData => ({
        ...prevData,
        datasets: [{
          ...prevData.datasets[0],
          data: [
            statusCounts30Days[PurchaseOrderStatus.Pending] || 0,
            statusCounts30Days[PurchaseOrderStatus.InProgress] || 0,
            statusCounts30Days[PurchaseOrderStatus.Completed] || 0,
            statusCounts30Days[PurchaseOrderStatus.Rejected] || 0
          ]
        }]
      }));

      setPurchaseOrders(orders.slice(0, 10));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#495057'
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const statusBodyTemplate = (rowData: PurchaseOrder) => {
    return <span className={`customer-badge status-${rowData.status.toLowerCase()}`}>{rowData.status}</span>;
  };

  const actionBodyTemplate = (rowData: PurchaseOrder) => {
    return (
      <Link to={`/purchase-orders/${rowData.id}`}>
        <Button icon="pi pi-eye" className="p-button-rounded p-button-info" />
      </Link>
    );
  };

  const calculateTotalAmount = (po: PurchaseOrder) => {
    if (!po.items || po.items.length === 0) {
      return 0;
    }
    return po.items.reduce((total, item) => {
      const unitPrice = parseFloat(item.unitPrice.toString());
      const quantity = parseFloat(item.quantity.toString());
      return total + (unitPrice * quantity);
    }, 0);
  };

  const handlingUnitsTemplate = (rowData: PurchaseOrder) => {
    const huNumbers = rowData.items
      ?.flatMap(item => item.handlingUnits?.map(hu => hu.huNumber) || [])
      .filter((huNumber, index, self) => self.indexOf(huNumber) === index)
      .join(', ');
    return huNumbers || 'No HUs';
  };

  const header = (
    <div className="flex justify-content-between align-items-center">
      <h5 className="m-0">Recent Purchase Orders</h5>
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search PO or HU"
        />
      </span>
    </div>
  );

  return (
    <div className="grid">
      {/* Purchase Orders Table */}
      <div className="col-12">
        <Card>
          <DataTable
            value={purchaseOrders}
            loading={loading}
            header={header}
            className="p-datatable-sm"
            emptyMessage="No purchase orders found."
          >
            <Column field="poNumber" header="PO Number" sortable />
            <Column field="client.name" header="Client" sortable body={(rowData: PurchaseOrder) => rowData.client?.name || 'N/A'} />
            <Column field="status" header="Status" sortable body={statusBodyTemplate} />
            <Column field="totalAmount" header="Total Amount" sortable body={(rowData: PurchaseOrder) => formatCurrency(calculateTotalAmount(rowData))} />
            <Column field="handlingUnits" header="Handling Units" body={handlingUnitsTemplate} />
            <Column field="createdAt" header="Created At" sortable body={(rowData: PurchaseOrder) => formatDate(rowData.createdAt)} />
            <Column body={actionBodyTemplate} style={{ width: '4rem' }} />
          </DataTable>
        </Card>
      </div>

      {/* Charts */}
      <div className="col-12 lg:col-6">
        <Card title="Purchase Order Status (Last 14 Days)" style={{ height: '400px' }}>
          <div style={{ height: 'calc(400px - 4rem)' }}>
            <Chart type="pie" data={chartData14Days} options={chartOptions} />
          </div>
        </Card>
      </div>

      <div className="col-12 lg:col-6">
        <Card title="Purchase Order Status (Last 30 Days)" style={{ height: '400px' }}>
          <div style={{ height: 'calc(400px - 4rem)' }}>
            <Chart type="pie" data={chartData30Days} options={chartOptions} />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
